/**
 * Features Activation Page - Admin
 * Clone of Alora Plus Features Activation page
 * Allows administrators to view and request activation of system features
 */

import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import api from '../../api';
import {
  Zap, CheckCircle, XCircle, Clock, Send, RefreshCw,
  Smartphone, FileText, DollarSign, Shield, Users, Settings,
  Handshake, Activity, Calendar, Phone, Mail, Database,
  ClipboardCheck, Stethoscope, Heart, Pill, AlertTriangle
} from 'lucide-react';

const FeaturesActivationPage = () => {
  const [loading, setLoading] = useState(true);
  const [features, setFeatures] = useState([]);
  const [requestingFeature, setRequestingFeature] = useState(null);

  const featureCategories = [
    {
      name: 'Workflow Efficiency',
      icon: Activity,
      color: 'blue',
      features: [
        { id: 'careconnect_evv', name: 'CareConnect/EVV', description: 'Electronic Visit Verification system for caregiver tracking', activated: false },
        { id: 'a_fax', name: 'A-Fax', description: 'Integrated fax management system', activated: false },
        { id: 'telephony', name: 'Telephony', description: 'Phone system integration for automated calls', activated: true },
        { id: 'mobile_app', name: 'Mobile Point of Care', description: 'Mobile application for field staff', activated: true },
        { id: 'scheduler_plus', name: 'Scheduler Plus', description: 'Advanced scheduling features', activated: false },
      ]
    },
    {
      name: 'Documentation',
      icon: FileText,
      color: 'green',
      features: [
        { id: 'custom_documents', name: 'Custom Documents', description: 'Create and manage custom document templates', activated: true },
        { id: 'e_signature', name: 'E-Signature', description: 'Electronic signature capture', activated: true },
        { id: 'document_scanning', name: 'Document Scanning', description: 'Scan and attach documents to patient records', activated: false },
        { id: 'oasis_automation', name: 'OASIS Automation', description: 'Automated OASIS form completion', activated: false },
      ]
    },
    {
      name: 'Billing Solutions',
      icon: DollarSign,
      color: 'purple',
      features: [
        { id: 'bill_pay', name: 'Alora Bill & Pay', description: 'Integrated billing and payment processing', activated: false },
        { id: 'electronic_claims', name: 'Electronic Claims', description: 'Submit claims electronically to payers', activated: true },
        { id: 'era_posting', name: 'ERA Auto-Posting', description: 'Automatic posting of electronic remittance advice', activated: false },
        { id: 'patient_billing', name: 'Patient Billing', description: 'Generate and manage patient invoices', activated: true },
      ]
    },
    {
      name: 'Medicare Integration',
      icon: Shield,
      color: 'red',
      features: [
        { id: 'medicare_dde', name: 'Medicare DDE Integration', description: 'Direct Data Entry portal integration', activated: false },
        { id: 'ngs_integration', name: 'NGS Integration', description: 'National Government Services integration', activated: false },
        { id: 'palmetto_integration', name: 'Palmetto Integration', description: 'Palmetto GBA integration', activated: false },
        { id: 'cms_reporting', name: 'CMS Reporting', description: 'Centers for Medicare & Medicaid Services reporting', activated: false },
      ]
    },
    {
      name: 'Special Configurations',
      icon: Settings,
      color: 'orange',
      features: [
        { id: 'hospice_module', name: 'Hospice Module', description: 'Specialized hospice care management', activated: false },
        { id: 'pediatric_module', name: 'Pediatric Module', description: 'Pediatric home health care features', activated: false },
        { id: 'multi_branch', name: 'Multi-Branch', description: 'Manage multiple locations', activated: false },
        { id: 'advanced_reporting', name: 'Advanced Reporting', description: 'Custom report builder and analytics', activated: true },
      ]
    },
    {
      name: 'Partner Solutions',
      icon: Handshake,
      color: 'teal',
      features: [
        { id: 'inovalon', name: 'Inovalon Integration', description: 'Inovalon claim portal integration', activated: true },
        { id: 'waystar', name: 'Waystar Integration', description: 'Waystar revenue cycle management', activated: false },
        { id: 'availity', name: 'Availity Integration', description: 'Availity essentials integration', activated: false },
        { id: 'surescripts', name: 'SureScripts', description: 'E-prescribing network integration', activated: false },
      ]
    },
  ];

  useEffect(() => {
    loadFeatures();
  }, []);

  const loadFeatures = async () => {
    try {
      const response = await api.get('/settings/features');
      if (response.data?.features) {
        setFeatures(response.data.features);
      }
    } catch (error) {
      console.log('Using default features');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestActivation = async (featureId, featureName) => {
    setRequestingFeature(featureId);
    try {
      await api.post('/settings/features/request', {
        featureId,
        featureName
      });
      toast.success(`Activation requested for ${featureName}. We'll respond within 1-2 business days.`);
    } catch (error) {
      toast.error('Failed to submit activation request');
    } finally {
      setRequestingFeature(null);
    }
  };

  const getFeatureStatus = (featureId) => {
    const saved = features.find(f => f.id === featureId);
    if (saved) return saved.activated;
    
    // Check default from categories
    for (const cat of featureCategories) {
      const feature = cat.features.find(f => f.id === featureId);
      if (feature) return feature.activated;
    }
    return false;
  };

  const colorClasses = {
    blue: 'bg-blue-100 text-blue-700 border-blue-200',
    green: 'bg-green-100 text-green-700 border-green-200',
    purple: 'bg-purple-100 text-purple-700 border-purple-200',
    red: 'bg-red-100 text-red-700 border-red-200',
    orange: 'bg-orange-100 text-orange-700 border-orange-200',
    teal: 'bg-teal-100 text-teal-700 border-teal-200',
  };

  const activatedCount = featureCategories.reduce((acc, cat) => {
    return acc + cat.features.filter(f => getFeatureStatus(f.id)).length;
  }, 0);

  const totalFeatures = featureCategories.reduce((acc, cat) => acc + cat.features.length, 0);

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="features-activation-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Zap className="w-6 h-6 text-yellow-500" />
              Features Activation
            </h1>
            <p className="text-gray-600 mt-1">Manage and activate system features</p>
          </div>
          <Button 
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="request-licenses-btn"
          >
            <Send className="w-4 h-4 mr-2" />
            Request Additional Licenses
          </Button>
        </div>

        {/* Summary */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Activity className="w-5 h-5 text-blue-600" />
            <span className="font-medium text-blue-800">
              {activatedCount} of {totalFeatures} features activated
            </span>
          </div>
          <div className="text-sm text-blue-600">
            <Clock className="w-4 h-4 inline mr-1" />
            NOTE: All requests will be responded to within 1 – 2 business days.
          </div>
        </div>

        {/* Feature Categories */}
        {loading ? (
          <div className="text-center py-8">
            <RefreshCw className="w-6 h-6 animate-spin mx-auto text-blue-600" />
            <p className="mt-2 text-gray-600">Loading features...</p>
          </div>
        ) : (
          <div className="space-y-6">
            {featureCategories.map((category) => (
              <div key={category.name} className="bg-white rounded-lg border overflow-hidden">
                <div className={`px-6 py-4 border-b ${colorClasses[category.color]}`}>
                  <h2 className="font-semibold flex items-center gap-2">
                    <category.icon className="w-5 h-5" />
                    {category.name}
                  </h2>
                </div>
                <div className="divide-y">
                  {category.features.map((feature) => {
                    const isActivated = getFeatureStatus(feature.id);
                    return (
                      <div 
                        key={feature.id} 
                        className="px-6 py-4 flex items-center justify-between hover:bg-gray-50"
                        data-testid={`feature-${feature.id}`}
                      >
                        <div className="flex-1">
                          <h3 className="font-medium text-gray-900">{feature.name}</h3>
                          <p className="text-sm text-gray-500 mt-1">{feature.description}</p>
                        </div>
                        <div className="ml-4">
                          {isActivated ? (
                            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-700">
                              <CheckCircle className="w-4 h-4" />
                              Activated
                            </span>
                          ) : (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleRequestActivation(feature.id, feature.name)}
                              disabled={requestingFeature === feature.id}
                              className="text-orange-600 border-orange-300 hover:bg-orange-50"
                              data-testid={`request-${feature.id}`}
                            >
                              {requestingFeature === feature.id ? (
                                <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
                              ) : (
                                <XCircle className="w-4 h-4 mr-1" />
                              )}
                              Not Activated
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Info Note */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-yellow-800">Feature Activation Information</p>
              <p className="text-sm text-yellow-700 mt-1">
                Some features may require additional licensing or configuration. Click on any 
                &quot;Not Activated&quot; feature to submit an activation request. Our team will review 
                your request and contact you regarding any additional requirements or costs.
              </p>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default FeaturesActivationPage;
